// Self-service signup. Email + password only — company info is collected
// in the OnboardingWizard immediately after, so the form here stays
// short and frictionless.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@erp/ui';

export function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      // Normalize identically to LoginPage (lowercase + trim) so a
      // later signin matches the row Supabase wrote here, regardless
      // of capitalization or stray whitespace from autofill.
      const autoLoggedIn = await signup(email.trim().toLowerCase(), password);
      if (autoLoggedIn) {
        // Skip the email-confirmation gate — head straight to the wizard.
        navigate('/welcome', { replace: true });
      } else {
        // Supabase project requires confirmation. Tell the user to check
        // their inbox; once they click the link they'll come back through
        // /login and reach the wizard from there.
        setPendingConfirmation(true);
      }
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (pendingConfirmation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">📬 Vérifiez votre email</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              On vient d'envoyer un lien de confirmation à <strong>{email}</strong>.
              Cliquez dessus pour activer votre compte.
            </p>
            <p>
              Pas reçu ? Vérifiez vos spams. Le lien expire dans 24 h —
              recommencez l'inscription au besoin.
            </p>
            <div className="pt-2">
              <Link to="/login" className="text-primary underline">
                Retour à la connexion
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Créez votre boutique</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Gratuit. Pas de carte bancaire. 30 secondes.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="su-email">Email</Label>
              <Input
                id="su-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="su-password">Mot de passe</Label>
              <Input
                id="su-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground">8 caractères minimum.</p>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Création…' : "Créer mon compte"}
            </Button>
          </form>

          <div className="mt-4 pt-4 border-t text-center text-sm text-muted-foreground">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Se connecter
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function humanizeError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  if (msg.toLowerCase().includes('already registered')) {
    return 'Cet email est déjà utilisé. Connectez-vous plutôt.';
  }
  if (msg.toLowerCase().includes('weak password') || msg.toLowerCase().includes('password')) {
    return 'Mot de passe trop faible. Utilisez au moins 8 caractères.';
  }
  if (msg.toLowerCase().includes('email')) {
    return "L'email est invalide.";
  }
  return msg;
}
